// /q/:token (served on go.<domain>/q/<token>, and on the console's own hosts):
// the customer's quote. Public: no sign-in, the long random token in the link
// is the key (public API proposals.py). Loads the frozen snapshot and lets the
// customer accept or decline it.
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { proposalApi, type ProposalState } from "../../quotes/api";
import { ProposalView, type AcceptChoice } from "../../quotes/ProposalView";

export function ProposalPage() {
  const { token = "" } = useParams();
  const [state, setState] = useState<ProposalState | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    proposalApi
      .get(token)
      .then((s) => {
        setState(s);
        document.title = `Heat pump quote ${s.proposal.reference} · ${s.proposal.company.companyName}`;
      })
      .catch((e: Error) => setError(e.message));
  }, [token]);

  useEffect(load, [load]);

  async function accept(choice: AcceptChoice) {
    const res = await proposalApi.accept(token, choice);
    setState((s) => (s ? { ...s, status: "accepted", acceptance: res.acceptance } : s));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function decline(reason: string) {
    await proposalApi.decline(token, reason);
    setState((s) => (s ? { ...s, status: "declined", decline: { at: new Date().toISOString() } } : s));
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  if (error) {
    return (
      <div className="confirm-page">
        <div className="confirm-card">
          <h1>Quote not found</h1>
          <p>{error}</p>
          <p className="muted">If you were sent this link, please ask the installer for a new one.</p>
        </div>
      </div>
    );
  }
  if (!state) {
    return (
      <div className="confirm-page">
        <p className="muted">Loading your quote…</p>
      </div>
    );
  }
  return (
    <ProposalView
      // Remount when the answer changes so the page shows the accepted choice
      key={state.status}
      proposal={state.proposal}
      status={state.status}
      expired={state.expired}
      acceptance={state.acceptance}
      declined={Boolean(state.decline)}
      onAccept={accept}
      onDecline={decline}
    />
  );
}
